import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Loader2, FileText, Save, Plus, Trash2, AlertCircle, Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { key: "faq", label: "FAQ", type: "faq" },
  { key: "canned_responses", label: "Canned responses", type: "canned" },
  { key: "privacy", label: "Privacy", type: "privacy" },
  { key: "medical_disclaimer", label: "Medical disclaimer", type: "text" },
  { key: "current_features", label: "Current features", type: "list" },
  { key: "roadmap_features", label: "Roadmap features", type: "list" },
  { key: "forbidden_claims", label: "Forbidden claims", type: "list" },
  { key: "must_never_say", label: "Must never say", type: "list" },
  { key: "crisis_keywords", label: "Crisis keywords", type: "list" },
  { key: "behavior_rules", label: "Behavior rules", type: "list" },
];

const PRIVACY_FIELDS = [
  { key: "data_collected", label: "Data collected" },
  { key: "data_purpose", label: "Purpose of data" },
  { key: "protection", label: "Protection measures" },
  { key: "access", label: "Access control" },
];

const CANNED_FIELDS = [
  { key: "out_of_scope", label: "Out of scope / unknown" },
  { key: "pricing", label: "Pricing question" },
  { key: "diagnosis_request", label: "Diagnosis request" },
  { key: "medication", label: "Medication advice" },
  { key: "crisis", label: "Crisis / emergency (hard short-circuit)" },
];

function clone(v) { return JSON.parse(JSON.stringify(v)); }

export default function AdminContentManagerPage() {
  const [content, setContent] = useState(null); // { version, last_updated, sections }
  const [draft, setDraft] = useState(null); // mutable sections
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingKey, setSavingKey] = useState(null);
  const [activeTab, setActiveTab] = useState("faq");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/content`);
      setContent(res.data);
      setDraft(clone(res.data.sections));
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load content");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSection = async (sectionKey) => {
    setSavingKey(sectionKey);
    try {
      await axios.put(`${API}/admin/content/section`, {
        section: sectionKey,
        value: draft[sectionKey],
      });
      toast.success("Saved");
      setContent((prev) => ({
        ...prev,
        sections: { ...prev.sections, [sectionKey]: clone(draft[sectionKey]) },
        last_updated: new Date().toISOString().slice(0, 10),
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingKey(null);
    }
  };

  const resetSection = (sectionKey) => {
    if (!content) return;
    setDraft((prev) => ({ ...prev, [sectionKey]: clone(content.sections[sectionKey]) }));
  };

  const isDirty = (sectionKey) => {
    if (!content) return false;
    return JSON.stringify(draft?.[sectionKey]) !== JSON.stringify(content.sections[sectionKey]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading content…
      </div>
    );
  }
  if (error) {
    return <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">{error}</div>;
  }

  return (
    <div className="space-y-5" data-testid="admin-content-manager">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5" /> Content Manager
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Edit the AI Assistant knowledge base. Changes apply immediately to every new conversation.
          </p>
        </div>
        <div className="text-xs text-slate-500">
          KB version <code className="bg-slate-100 px-1 rounded">{content.version}</code>
          {content.last_updated && <> · updated {content.last_updated}</>}
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          Website page copy (hero, features, testimonials, etc.) remains managed through the localized translation files for safety.
          This editor only modifies the assistant knowledge base (FAQ, disclaimers, canned responses, safety rules).
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((t) => {
          const dirty = isDirty(t.key);
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              data-testid={`content-tab-${t.key}`}
              className={`relative px-3 py-2 text-sm font-medium transition-colors rounded-t-md ${
                activeTab === t.key
                  ? "text-cyan-700 border-b-2 border-cyan-600"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {t.label}
              {dirty && <span className="ml-1.5 inline-block w-1.5 h-1.5 bg-amber-500 rounded-full align-middle" />}
            </button>
          );
        })}
      </div>

      {TABS.filter((t) => t.key === activeTab).map((tab) => (
        <div key={tab.key} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`content-panel-${tab.key}`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-800">{tab.label}</h2>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => resetSection(tab.key)}
                disabled={!isDirty(tab.key) || savingKey === tab.key}
              >
                Reset
              </Button>
              <Button
                size="sm"
                onClick={() => saveSection(tab.key)}
                disabled={!isDirty(tab.key) || savingKey === tab.key}
                data-testid={`content-save-${tab.key}`}
                className="bg-cyan-700 hover:bg-cyan-800 text-white"
              >
                {savingKey === tab.key ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                Save
              </Button>
            </div>
          </div>

          {tab.type === "faq" && (
            <FaqEditor
              items={draft[tab.key] || []}
              onChange={(v) => setDraft((p) => ({ ...p, [tab.key]: v }))}
            />
          )}

          {tab.type === "canned" && (
            <DictEditor
              fields={CANNED_FIELDS}
              value={draft[tab.key] || {}}
              onChange={(v) => setDraft((p) => ({ ...p, [tab.key]: v }))}
              multiline
            />
          )}

          {tab.type === "privacy" && (
            <DictEditor
              fields={PRIVACY_FIELDS}
              value={draft[tab.key] || {}}
              onChange={(v) => setDraft((p) => ({ ...p, [tab.key]: v }))}
              multiline
            />
          )}

          {tab.type === "text" && (
            <TextAreaEditor
              value={draft[tab.key] || ""}
              onChange={(v) => setDraft((p) => ({ ...p, [tab.key]: v }))}
              minRows={5}
              testid={`content-text-${tab.key}`}
            />
          )}

          {tab.type === "list" && (
            <ListEditor
              items={draft[tab.key] || []}
              onChange={(v) => setDraft((p) => ({ ...p, [tab.key]: v }))}
              testid={`content-list-${tab.key}`}
            />
          )}

          {["crisis_keywords", "must_never_say", "forbidden_claims"].includes(tab.key) && (
            <div className="mt-4 flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              Safety-critical list. Removing entries may disable safeguards used by the assistant.
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function FaqEditor({ items, onChange }) {
  const update = (idx, field, val) => {
    const next = items.map((it, i) => (i === idx ? { ...it, [field]: val } : it));
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, { question: "", answer: "" }]);

  return (
    <div className="space-y-3">
      {items.map((it, idx) => (
        <div key={idx} className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50" data-testid={`content-faq-item-${idx}`}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider">FAQ #{idx + 1}</span>
            <button
              onClick={() => remove(idx)}
              className="text-slate-400 hover:text-red-600"
              aria-label="Remove FAQ"
              data-testid={`content-faq-remove-${idx}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
          <input
            type="text"
            value={it.question}
            onChange={(e) => update(idx, "question", e.target.value)}
            placeholder="Question"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
            data-testid={`content-faq-q-${idx}`}
          />
          <textarea
            value={it.answer}
            onChange={(e) => update(idx, "answer", e.target.value)}
            placeholder="Answer"
            rows={3}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white resize-y"
            data-testid={`content-faq-a-${idx}`}
          />
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} data-testid="content-faq-add">
        <Plus className="w-4 h-4 mr-1.5" /> Add FAQ
      </Button>
    </div>
  );
}

function DictEditor({ fields, value, onChange, multiline }) {
  return (
    <div className="space-y-3">
      {fields.map((f) => (
        <div key={f.key} className="space-y-1">
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{f.label}</label>
          {multiline ? (
            <textarea
              value={value[f.key] || ""}
              onChange={(e) => onChange({ ...value, [f.key]: e.target.value })}
              rows={3}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white resize-y"
              data-testid={`content-dict-${f.key}`}
            />
          ) : (
            <input
              type="text"
              value={value[f.key] || ""}
              onChange={(e) => onChange({ ...value, [f.key]: e.target.value })}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
              data-testid={`content-dict-${f.key}`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function TextAreaEditor({ value, onChange, minRows = 4, testid }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={minRows}
      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white resize-y"
      data-testid={testid}
    />
  );
}

function ListEditor({ items, onChange, testid }) {
  const update = (idx, val) => onChange(items.map((it, i) => (i === idx ? val : it)));
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, ""]);

  return (
    <div className="space-y-2" data-testid={testid}>
      {items.map((it, idx) => (
        <div key={idx} className="flex items-start gap-2">
          <span className="text-xs text-slate-400 mt-2.5 w-6 text-right">{idx + 1}.</span>
          <input
            type="text"
            value={it}
            onChange={(e) => update(idx, e.target.value)}
            className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
            data-testid={`${testid}-item-${idx}`}
          />
          <button
            onClick={() => remove(idx)}
            className="text-slate-400 hover:text-red-600 mt-1"
            aria-label="Remove item"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} data-testid={`${testid}-add`}>
        <Plus className="w-4 h-4 mr-1.5" /> Add item
      </Button>
    </div>
  );
}
